
/**
 * ProductAddons - Manages addon product variant selections
 * 
 * This custom element handles variant changes within addon products in the bundle section.
 * Each product-addons element can contain multiple products, each with their own variants.
 * 
 * When a variant is selected in an addon product:
 * - Updates the checkbox value to the new variant ID
 * - Updates the data-price attribute
 * - Updates the product image
 * - Updates the variant title display
 * - Updates the price display
 * - Handles availability (enables/disables checkbox)
 * 
 * @example
 * <product-addons id="ProductBundle-{{ section.id }}-{{ product.id }}">
 *   <!-- Multiple addon products with variant pickers -->
 * </product-addons>
 */
class ProductAddons extends HTMLElement {
  constructor() {
    super();
    this.boundHandleVariantChange = this.handleVariantChange.bind(this);
  }

  connectedCallback() {
    // Listen for all variant:change events on the document
    document.addEventListener('variant:change', this.boundHandleVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.boundHandleVariantChange);
  }

  /**
   * Handle variant change events, but only process if it's from an addon context
   * within this product-addons element
   * @param {CustomEvent} event - The variant:change event
   */
  handleVariantChange(event) {
    const originalTrigger = event.detail.originalTrigger;
    
    // Only process if this is from an addon context
    if (!originalTrigger || originalTrigger.context !== 'addon') {
      return;
    }

    // Check if the variant picker is within this product-addons element
    const variantPicker = originalTrigger.variantPicker;
    if (!variantPicker || !this.contains(variantPicker)) {
      return;
    }

    // Find the product wrapper (the label containing this variant picker)
    const productLabel = variantPicker.closest('.product-bundle__info');
    if (!productLabel) {
      return;
    }

    // Get the new variant from the event
    const newVariant = event.detail.variant;
    if (!newVariant) {
      return;
    }

    this.updateAddonProduct(productLabel, newVariant);
  }

  /**
   * Update the addon product UI with the new variant information
   * @param {HTMLElement} productLabel - The label element containing the addon product
   * @param {Object} variant - The new variant object with id, price, title, available, etc.
   */
  updateAddonProduct(productLabel, variant) {
    // Update the checkbox (input[name="bundles"]) value and attributes
    this.updateCheckbox(productLabel, variant);

    // Update the variant image
    this.updateVariantImage(productLabel, variant);

    // Update the variant title display (if shown)
    this.updateVariantTitle(productLabel, variant);

    // Update the price display
    this.updatePrice(productLabel, variant);
  }

  /**
   * Update the checkbox value, ID, and availability
   * @param {HTMLElement} productLabel - The label element
   * @param {Object} variant - The variant object
   */
  updateCheckbox(productLabel, variant) {
    const checkbox = productLabel.querySelector('input[name="bundles"]');
    if (!checkbox) return;

    const wasChecked = checkbox.checked;
    
    // Update the value to the new variant ID
    checkbox.value = variant.id;
    checkbox.setAttribute('value', variant.id);
    checkbox.setAttribute('data-price', variant.price);
    
    // Update the ID to match the new variant
    const idPrefix = checkbox.id.split('-').slice(0, -1).join('-');
    const newId = `${idPrefix}-${variant.id}`;
    checkbox.id = newId;
    productLabel.setAttribute('for', newId);
    
    // Handle availability
    if (variant.available) {
      checkbox.disabled = false;
      checkbox.removeAttribute('disabled');
    } else {
      checkbox.disabled = true;
      checkbox.setAttribute('disabled', '');
      // Uncheck if not available
      if (wasChecked) {
        checkbox.checked = false;
      }
    }
  }

  /**
   * Update the addon product image
   * @param {HTMLElement} productLabel - The label element
   * @param {Object} variant - The variant object
   */
  updateVariantImage(productLabel, variant) {
    const imageContainer = productLabel.querySelector('.horizontal-product__media');
    if (!imageContainer) return;

    const currentImage = imageContainer.querySelector('img');
    if (!currentImage) return;

    // Only update if variant has a featured image
    if (!variant.featured_media || !variant.featured_media.preview_image) {
      return;
    }

    // Create new image element with the variant's featured image
    const newImage = currentImage.cloneNode(false);
    newImage.src = variant.featured_media.preview_image.src;
    newImage.srcset = this.generateSrcset(variant.featured_media.preview_image);
    newImage.alt = variant.featured_media.alt || variant.name || '';
    
    imageContainer.replaceChildren(newImage);
  }

  /**
   * Update the variant title text (e.g., "Small / Red")
   * @param {HTMLElement} productLabel - The label element
   * @param {Object} variant - The variant object
   */
  updateVariantTitle(productLabel, variant) {
    const variantTitleSpan = productLabel.querySelector('.horizontal-product__details .text-xs.text-opacity');
    if (!variantTitleSpan) return;

    // Only show title if it's not a default variant
    if (variant.title && variant.title !== 'Default Title') {
      variantTitleSpan.textContent = variant.title;
      variantTitleSpan.style.display = '';
    } else {
      variantTitleSpan.style.display = 'none';
    }
  }

  /**
   * Update the price display
   * @param {HTMLElement} productLabel - The label element
   * @param {Object} variant - The variant object
   */
  updatePrice(productLabel, variant) {
    // Product enhancements block: inline "+£xx" price next to the title
    const addonPrice = productLabel.querySelector('[data-addon-price]');
    if (addonPrice) {
      addonPrice.textContent = variant.available
        ? `+${this.formatMoney(variant.price)}`
        : (window.theme?.variantStrings?.soldOut || 'Sold out');
      return;
    }

    const priceContainer = productLabel.querySelector('.horizontal-product__quantity');
    if (!priceContainer) return;

    if (variant.available) {
      // Find or create the price element
      let priceElement = priceContainer.querySelector('.price');
      if (!priceElement) {
        priceElement = document.createElement('div');
        priceElement.className = 'price flex flex-wrap items-baseline gap-2 lg:flex-col xl:items-end lg:gap-1-5';
        priceContainer.innerHTML = '';
        priceContainer.appendChild(priceElement);
      }

      // Format the prices
      const formattedPrice = this.formatMoney(variant.price);
      const compareAtPrice = variant.compare_at_price;

      let priceHtml = '';
      
      if (compareAtPrice && compareAtPrice > variant.price) {
        // Show sale price
        const formattedComparePrice = this.formatMoney(compareAtPrice);
        priceHtml = `
          <span class="price__sale">${formattedComparePrice}</span>
          <span class="price__regular">${formattedPrice}</span>
        `;
      } else {
        // Show regular price
        priceHtml = `<span class="price__regular">${formattedPrice}</span>`;
      }

      priceElement.innerHTML = priceHtml;
    } else {
      // Show sold out message
      const soldOutText = window.theme?.variantStrings?.soldOut || 'Sold out';
      priceContainer.innerHTML = `<span class="text-sm text-opacity">${soldOutText}</span>`;
    }
  }

  /**
   * Generate srcset for responsive images
   * @param {Object} image - The image object with src property
   * @returns {string} The srcset string
   */
  generateSrcset(image) {
    const widths = [180, 360, 540];
    const srcset = widths
      .map(width => {
        // Shopify image URL pattern: replace file extension with _{width}x.extension
        const url = image.src.replace(/(\.[^.]+)$/, `_${width}x$1`);
        return `${url} ${width}w`;
      })
      .join(', ');
    return srcset;
  }

  /**
   * Format money using the theme's currency formatter
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price string
   */
  formatMoney(cents) {
    // Use theme's currency formatter if available
    if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
      return window.theme.Currency.formatMoney(cents, window.theme.moneyFormat);
    }
    
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Register the custom element
if (!customElements.get('product-addons')) {
  customElements.define('product-addons', ProductAddons);
}


/**
 * ProductEnhancements - Addon list rendered as standalone cards (no accordion)
 *
 * Takes over the job ProductBundleDetails does for the accordion version:
 * dispatches `bundle:change` on the product form so the buy button total
 * updates, and clears selections once the main product has been added to cart.
 *
 * @example
 * <product-enhancements id="ProductEnhancements-{{ section.id }}-{{ product.id }}" form="{{ product_form_id }}">
 *   <!-- Addon cards with input[name="bundles"] switches -->
 * </product-enhancements>
 */
class ProductEnhancements extends ProductAddons {
  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get bundles() {
    return Array.from(this.querySelectorAll('input[name="bundles"]:checked'));
  }

  connectedCallback() {
    super.connectedCallback();
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    this.boundOnBundleChange = this.onBundleChange.bind(this);
    this.addEventListener('change', this.boundOnBundleChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    this.removeEventListener('change', this.boundOnBundleChange);
  }

  onCartUpdate(event) {
    if (this.bundles.length === 0) return;

    if (event.source === 'product-form') {
      this.bundles.forEach((input) => {
        if (!input.disabled) input.checked = false;
      });
      this.onBundleChange();
    }
  }

  // Re-dispatch after a variant swap so the total picks up the new price
  updateAddonProduct(productLabel, variant) {
    super.updateAddonProduct(productLabel, variant);
    this.onBundleChange();
  }

  onBundleChange() {
    (this.productForm ?? document).dispatchEvent(new CustomEvent('bundle:change', {
      detail: {
        items: this.bundles.map(input => ({
          id: input.value,
          quantity: 1,
          price: input.getAttribute('data-price')
        }))
      }
    }));
  }
}

if (!customElements.get('product-enhancements')) {
  customElements.define('product-enhancements', ProductEnhancements);
}
